import React, {useState, useEffect, useMemo} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, Form, Button } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_ADMIN, END_POINT_ROLES } from "../const";
import { useNavigate, Link } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { FormikSearchableSelect, buildSelectOptions } from '../components/SearchableSelect';
import VendorPermissionFields from '../components/VendorPermissionFields';
import UserPermissionFields from '../components/UserPermissionFields';
import QuoteViewUsersField from '../components/QuoteViewUsersField';
import AccountStatusField from '../components/AccountStatusField';
import useAuthenticatedAdmin from '../hooks/useAuthenticatedAdmin';
import { hasUserPermission, isSuperAdmin as isSuperAdminAccount, isVendorOwner } from '../utils/userPermissions';

export const CreateAdminForm = () => {
    const { admin: loggedInAdmin, loading: accessLoading } = useAuthenticatedAdmin();
    const isSuperAdmin = isSuperAdminAccount(loggedInAdmin);
    const canManageUserPermissions = isVendorOwner(loggedInAdmin) && !isSuperAdmin;
    const canCreateUser = hasUserPermission('users', 'add', loggedInAdmin);
    const [submitError, setSubmitError] = useState('');

    const rawToken = sessionStorage.getItem('quotetoken');
    const token = rawToken?.replace(/^"|"$/g, '');

    const [role, setRole] = useState([]);
    const [teammates, setTeammates] = useState([]);

    const navigate = useNavigate();

    useEffect(() => {
        if (accessLoading || isSuperAdmin) return; // Vendors are always Admin; no role list needed.
        const fetchRolelist = async () => {
          try {

            const response = await axios.get(API_URL + END_POINT_ROLES, {
              headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                Authorization: `Bearer ${token}`,
              }
            });

            if (response.data.status && Array.isArray(response.data.data)) {
              setRole(response.data.data);
            }

          } catch (error) {
            console.error('Error fetching tasks:', error);
          }

        };

        const timeout = setTimeout(() => {
          fetchRolelist();
        });
        return () => clearTimeout(timeout);

      }, [accessLoading, isSuperAdmin, token]);

  useEffect(() => {
    if (accessLoading || !canManageUserPermissions) return;
    let active = true;
    const fetchTeammates = async () => {
      try {
        const response = await axios.get(API_URL + END_POINT_ADMIN, {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
        });
        if (!active || !Array.isArray(response.data.data)) return;
        setTeammates(response.data.data.filter(user => !isSuperAdminAccount(user)));
      } catch (error) {
        if (active) setTeammates([]);
      }
    };
    fetchTeammates();
    return () => { active = false; };
  }, [accessLoading, canManageUserPermissions, token]);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      username: "",
      role_id: "",
      confirm_password: "",
      allow_multiple_companies: false,
      permissions: {},
      is_enabled: true,
      is_active: true,
      quote_view_user_ids: [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      username: Yup.string().required("Username is required"),
      password: Yup.string().required("Password is required").min(6, "6 letters required").max(10, "max 10 letters required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().matches(/^\d{10}$/, { message: "Phone must be exactly 10 digits", excludeEmptyString: true }),
      // Vendors are always created as Admin by the backend, so no role is chosen for them.
      role_id: isSuperAdmin ? Yup.string() : Yup.string().required("Role is required"),
      confirm_password: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref('password'), null], 'Confirm password does not match'),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      if (!canCreateUser) return;
      setSubmitError('');
      const payload = { ...values };
      if (isSuperAdmin) delete payload.role_id;
      if (!payload.phone) delete payload.phone;
      if (!isSuperAdmin) delete payload.allow_multiple_companies;
      if (isSuperAdmin) delete payload.is_active;
      else delete payload.is_enabled;
      if (!canManageUserPermissions) {
        delete payload.permissions;
        delete payload.quote_view_user_ids;
      } else {
        payload.quote_view_user_ids = (values.quote_view_user_ids || []).map(Number);
      }
      try {

        setSubmitting(true);

        const response = await axios.post(API_URL + END_POINT_ADMIN, payload,
            {
                headers: {
                    // 'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json',
                    Authorization: `Bearer ${token}`,
                }
            }
        );
        showSuccessToast(response.data.message)

        navigate('/admin');
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        setErrors(Object.fromEntries(Object.entries(errors).map(([field, messages]) =>
          [field, Array.isArray(messages) ? messages.join(' ') : messages])));
        const message = error.response?.data?.message || 'Failed to create user.';
        setSubmitError(message);
        showErrorToast(message);
      }finally {
        setSubmitting(false);
      }
    }
  });

  const roleOptions = useMemo(() => buildSelectOptions(role, {
    filter: (item) => String(item.name).trim().toLowerCase() !== 'super admin',
  }), [role]);

  // Vendors (Super Admin) are always Admin, so only ordinary-user forms choose a role.
  const showRoleField = !accessLoading && !isSuperAdmin;
  const usernameField = (
    <Col md={6} className="mb-3">
      <Form.Group controlId="username">
        <Form.Label>Username <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          {...formik.getFieldProps("username")}
          isInvalid={formik.touched.username && formik.errors.username}
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.username}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );

  return (
    <>
      <Card border="light" className="bg-white shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">{isSuperAdmin ? 'Add Vendor' : 'Add User'}</h5>
            <Link to="/admin" className="text-decoration-none btn-quote-back d-inline-flex align-items-center mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="feather feather-arrow-left-circle me-1"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 8 8 12 12 16"></polyline>
              <line x1="16" y1="12" x2="8" y2="12"></line>
            </svg>
            Back to Listing
          </Link>
          </div>

          {!accessLoading && !canCreateUser && <div role="alert" className="alert alert-danger">You do not have permission to add users.</div>}
          <Form onSubmit={formik.handleSubmit}>

            <Row>
              <Col md={6} className="mb-3">
                <Form.Group controlId="name">
                  <Form.Label>Name <span className="text-danger">*</span> </Form.Label>
                  <Form.Control
                    type="text"
                    {...formik.getFieldProps("name")}
                    isInvalid={formik.touched.name && formik.errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            
              <Col md={6} className="mb-3">
                <Form.Group controlId="email">
                  <Form.Label>Email <span className="text-danger">*</span> </Form.Label>
                  <Form.Control
                    type="email"
                    {...formik.getFieldProps("email")}
                    isInvalid={formik.touched.email && formik.errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group controlId="phone">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    {...formik.getFieldProps("phone")}
                    isInvalid={formik.touched.phone && formik.errors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {!showRoleField ? usernameField : (
                <Col sm={6} className="mb-3">
                  <Form.Group controlId="role_id">
                    <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                    <FormikSearchableSelect
                      formik={formik}
                      name="role_id"
                      options={roleOptions}
                      placeholder="Search role..."
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            {showRoleField && <Row>{usernameField}</Row>}

            <Row>
              <Col md={6} className="mb-3">
                <Form.Group controlId="password">
                  <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="password"
                    {...formik.getFieldProps("password")}
                    isInvalid={formik.touched.password && formik.errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group controlId="confirm_password">
                  <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="password"
                    {...formik.getFieldProps("confirm_password")}
                    isInvalid={
                      formik.touched.confirm_password && formik.errors.confirm_password
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.confirm_password}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {!accessLoading && canCreateUser && (
              <AccountStatusField formik={formik} accountType={isSuperAdmin ? 'vendor' : 'user'} />
            )}
            {isSuperAdmin && <VendorPermissionFields formik={formik} />}
            {canManageUserPermissions && <UserPermissionFields formik={formik} />}
            {canManageUserPermissions && (
              <QuoteViewUsersField
                users={teammates}
                selectedIds={formik.values.quote_view_user_ids}
                onChange={ids => formik.setFieldValue('quote_view_user_ids', ids)}
                disabled={formik.isSubmitting}
              />
            )}
            {submitError && <div role="alert" className="alert alert-danger">{submitError}</div>}

            <div className="mt-3">
              <Button 
                variant="primary" 
                type="submit"
                disabled={formik.isSubmitting || accessLoading || !canCreateUser}
              >
                Save
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};